import React, { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Collapse,
  IconButton,
  TableCell,
  TableRow,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import MergeTypeIcon from "@mui/icons-material/MergeType";

import AccountDetailCard from "./AccountDetailCard";
import MergePreviewDialog from "./MergePreviewDialog";

/** Coerce API row ids so recommended keeper and selection compare cleanly. */
const normalizeId = (v) => {
  if (v == null || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const mergeTypeForEntity = (entityType) => {
  if (entityType === "users") return "portal_user";
  if (entityType === "law_firms") return "law_firm";
  return "professional_guardian";
};

/**
 * One expandable row in a duplicate-groups table.
 *
 * Flow
 *  1. Click an active card to set the keeper (recommended is preselected when active).
 *  2. One loser → toolbar "Merge" opens preview for that pair.
 *  3. Multiple losers → expand row → "Merge loser {id}" per duplicate → preview → merge.
 *  4. Soft-deleted cards remain visible but are not keeper- or merge-eligible.
 */
const DuplicateGroupRow = ({
  group,
  entityType,
  isSelected,
  onSelectionChange,
  onMerged,
}) => {
  const [open, setOpen] = useState(false);
  const [previewState, setPreviewState] = useState({ open: false, loserId: null });

  const idField =
    entityType === "users"
      ? "user_id"
      : entityType === "law_firms"
      ? "lawfirm_id"
      : "pg_id";

  const accounts = group.accounts;

  /** Active = not soft-deleted. Only active accounts can be keeper / loser. */
  const activeAccountIds = useMemo(
    () =>
      accounts
        .filter((a) => !a.deleted_at)
        .map((a) => normalizeId(a[idField]))
        .filter((id) => id != null),
    [accounts, idField]
  );

  /** Default keeper = recommended (only if active), else the first active account. */
  const defaultKeeperId = useMemo(() => {
    const rec = normalizeId(group.recommended_keeper_id);
    if (rec != null && activeAccountIds.includes(rec)) return rec;
    return activeAccountIds[0] ?? null;
  }, [activeAccountIds, group.recommended_keeper_id]);

  const [keeperId, setKeeperId] = useState(defaultKeeperId);

  useEffect(() => {
    setKeeperId(defaultKeeperId);
  }, [defaultKeeperId, group.key]);

  const loserIds = useMemo(
    () => activeAccountIds.filter((id) => id !== keeperId),
    [activeAccountIds, keeperId]
  );

  const handleKeeperChange = (newKeeperId) => {
    const kid = normalizeId(newKeeperId);
    if (kid == null) return;
    if (!activeAccountIds.includes(kid)) return;
    setKeeperId(kid);
    if (isSelected && onSelectionChange) {
      onSelectionChange(group.key, kid, activeAccountIds.filter((id) => id !== kid));
    }
  };

  const handleCheckbox = (e) => {
    if (!onSelectionChange) return;
    if (e.target.checked) {
      onSelectionChange(group.key, keeperId, loserIds);
    } else {
      onSelectionChange(group.key, null, []);
    }
  };

  const handleToolbarMergeClick = () => {
    if (loserIds.length === 1) {
      setPreviewState({ open: true, loserId: loserIds[0] });
      return;
    }
    setOpen(true);
  };

  const summaryTitle = group.key || "(empty)";
  const totalActive = activeAccountIds.length;
  const totalSoftDeleted = accounts.length - totalActive;
  const mergeType = mergeTypeForEntity(entityType);

  return (
    <>
      <TableRow hover>
        <TableCell padding="checkbox">
          <Checkbox
            size="small"
            checked={!!isSelected}
            onChange={handleCheckbox}
            disabled={loserIds.length === 0}
          />
        </TableCell>
        <TableCell>
          <IconButton size="small" onClick={() => setOpen(!open)}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{summaryTitle}</Typography>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              {totalActive} active account{totalActive === 1 ? "" : "s"}
              {totalSoftDeleted > 0 ? ` · ${totalSoftDeleted} already removed` : ""}
            </Typography>
          </Box>
        </TableCell>
        <TableCell>
          {keeperId != null ? (
            <Chip size="small" label={`Keeper: ${keeperId}`} color="primary" variant="outlined" />
          ) : (
            <Chip size="small" label="No active keeper" color="default" variant="outlined" />
          )}
        </TableCell>
        <TableCell>
          <Chip
            size="small"
            label={`${loserIds.length} duplicate${loserIds.length === 1 ? "" : "s"}`}
            color="warning"
            variant="outlined"
          />
        </TableCell>
        <TableCell align="right">
          <Button
            size="small"
            variant="outlined"
            startIcon={<MergeTypeIcon fontSize="small" />}
            disabled={loserIds.length === 0 || keeperId == null}
            onClick={handleToolbarMergeClick}
          >
            {loserIds.length > 1 ? "Choose loser" : "Merge"}
          </Button>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={6} sx={{ py: 0, borderBottom: open ? undefined : "none" }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ p: 2, backgroundColor: "rgba(37,99,235,0.04)", borderRadius: 1, mb: 1 }}>
              <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Click an active card to choose the keeper, then merge each duplicate using the
                  actions below (preview shows bond and mapping counts before confirm).
                </Typography>
                <Typography variant="caption" sx={{ display: "block", color: "text.secondary" }}>
                  Soft-deleted accounts shown below were removed by a previous merge and cannot
                  be selected.
                </Typography>
              </Alert>

              <Grid container spacing={2}>
                {accounts.map((acc) => {
                  const accId = normalizeId(acc[idField]);
                  const isKeeper = keeperId != null && accId === keeperId;
                  return (
                    <Grid size={{ xs: 12, md: 6 }} key={accId ?? acc[idField]}>
                      <AccountDetailCard
                        account={acc}
                        entityType={entityType}
                        isKeeper={isKeeper}
                        isRecommended={accId === normalizeId(group.recommended_keeper_id)}
                        onSelectAsKeeper={handleKeeperChange}
                      />
                    </Grid>
                  );
                })}
              </Grid>

              {loserIds.length > 0 && keeperId != null && (
                <Box
                  sx={{
                    mt: 2,
                    pt: 1.5,
                    borderTop: "1px solid rgba(37, 99, 235, 0.2)",
                    backgroundColor: "rgba(255,255,255,0.7)",
                    borderRadius: 1,
                    px: 1,
                    py: 1,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{ color: "text.secondary", display: "block", mb: 0.75 }}
                  >
                    Merge into keeper <strong>{keeperId}</strong> — pick the duplicate to remove:
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                    {loserIds.map((lid) => (
                      <Button
                        key={lid}
                        size="small"
                        variant="contained"
                        color="primary"
                        startIcon={<MergeTypeIcon sx={{ fontSize: 16 }} />}
                        onClick={() => setPreviewState({ open: true, loserId: lid })}
                        sx={{ textTransform: "none" }}
                      >
                        Merge loser {lid}
                      </Button>
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>

      <MergePreviewDialog
        open={previewState.open}
        onClose={() => setPreviewState({ open: false, loserId: null })}
        mergeType={mergeType}
        keeperId={keeperId}
        loserId={previewState.loserId}
        onMerged={onMerged}
      />
    </>
  );
};

DuplicateGroupRow.propTypes = {
  group: PropTypes.shape({
    key: PropTypes.string,
    accounts: PropTypes.array,
    recommended_keeper_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }).isRequired,
  entityType: PropTypes.oneOf(["users", "law_firms", "pgs"]).isRequired,
  isSelected: PropTypes.bool,
  onSelectionChange: PropTypes.func,
  onMerged: PropTypes.func,
};

export default DuplicateGroupRow;
